import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Eye, Edit, Trash2, CheckCircle, XCircle, Calendar, DollarSign, Mail, RotateCcw, Archive } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import GettingStartedChecklist from './GettingStartedChecklist';

const EstimatesList = () => {
  const { user } = useAuth();
  const [estimates, setEstimates] = useState([]);
  const [deletedEstimates, setDeletedEstimates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sequenceStatuses, setSequenceStatuses] = useState({});
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'deleted'
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadEstimates();
  }, [filterStatus]);

  useEffect(() => {
    if (viewMode === 'deleted') {
      loadDeletedEstimates();
    }
  }, [viewMode]);

  const loadEstimates = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const params = { 
        user_id: user.id,
        _t: Date.now()
      };
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/estimates/`, { params });
      setEstimates(response.data);

      // Fetch sequence statuses for all loaded estimates
      if (response.data.length > 0) {
        const ids = response.data.map(e => e.id);
        try {
          const statusResp = await axios.post(`${BACKEND_URL}/api/unified-funnels/sequence-statuses`, { estimate_ids: ids });
          setSequenceStatuses(statusResp.data);
        } catch (err) {
          console.error('Error loading sequence statuses:', err);
        }
      }
    } catch (error) {
      console.error('Error loading estimates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDeletedEstimates = async () => {
    if (!user?.id) return;
    
    setIsLoadingDeleted(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/estimates/deleted/list`, {
        params: { user_id: user.id }
      });
      setDeletedEstimates(response.data);
    } catch (error) {
      console.error('Error loading deleted estimates:', error);
      toast.error('Failed to load deleted estimates');
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  const handleDelete = async (estimateId) => {
    if (!window.confirm('Are you sure you want to delete this estimate? It can be restored later from the Deleted tab.')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/estimates/${estimateId}`);
      toast.success('Estimate moved to deleted items');
      loadEstimates();
    } catch (error) {
      console.error('Error deleting estimate:', error);
      toast.error('Failed to delete estimate');
    }
  };

  const handleRestore = async (estimateId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/estimates/${estimateId}/restore`);
      toast.success('Estimate restored successfully');
      loadDeletedEstimates();
      loadEstimates();
    } catch (error) {
      console.error('Error restoring estimate:', error);
      toast.error('Failed to restore estimate');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredEstimates = estimates.filter(estimate => {
    const searchLower = searchTerm.toLowerCase();
    return (
      estimate.customer_last_name?.toLowerCase().includes(searchLower) ||
      estimate.address?.toLowerCase().includes(searchLower)
    );
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading estimates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Getting Started Checklist */}
      <GettingStartedChecklist />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Estimates</h1>
          <p className="text-gray-600 mt-1">Manage your roofing estimates and proposals</p>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/dashboard/my-rates">
            <Button className="bg-green-600 hover:bg-green-700 text-base px-6 py-3 shadow-md w-full">
              <DollarSign className="w-5 h-5 mr-2" />
              My Rates
            </Button>
          </Link>
          <Link to="/dashboard/estimates/new">
            <Button className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6 shadow-lg w-full">
              <Plus className="w-6 h-6 mr-2" />
              New Estimate
            </Button>
          </Link>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search by last name or address..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {/* View Mode Toggle */}
          <div className="flex border rounded-lg overflow-hidden mr-2">
            <button
              onClick={() => setViewMode('active')}
              className={`px-4 py-2 text-sm flex items-center gap-1 ${viewMode === 'active' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="view-active-btn"
            >
              Active
            </button>
            <button
              onClick={() => setViewMode('deleted')}
              className={`px-4 py-2 text-sm flex items-center gap-1 ${viewMode === 'deleted' ? 'bg-orange-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              data-testid="view-deleted-btn"
            >
              <Archive className="w-4 h-4" />
              Deleted ({deletedEstimates.length})
            </button>
          </div>
          
          {viewMode === 'active' && (
            <>
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                size="sm"
              >
                All
              </Button>
              <Button
                variant={filterStatus === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('pending')}
                size="sm"
              >
                Pending
              </Button>
              <Button
                variant={filterStatus === 'sent' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('sent')}
                size="sm"
              >
                Sent
              </Button>
              <Button
                variant={filterStatus === 'accepted' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('accepted')}
                size="sm"
              >
                Accepted
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Deleted Estimates View */}
      {viewMode === 'deleted' && (
        <div className="space-y-4">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Archive className="w-6 h-6 text-orange-600" />
              <div>
                <h2 className="font-semibold text-orange-800">Deleted Estimates</h2>
                <p className="text-sm text-orange-600">
                  {deletedEstimates.length} deleted estimate{deletedEstimates.length !== 1 ? 's' : ''}. Click restore to recover.
                </p>
              </div>
            </div>
          </div>

          {isLoadingDeleted ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
              Loading deleted estimates...
            </div>
          ) : deletedEstimates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <Archive className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No deleted estimates</p>
              <p className="text-sm text-gray-400 mt-2">
                Estimates you delete will appear here. You can restore them anytime.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Deleted On
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {deletedEstimates.map((estimate) => (
                    <tr key={estimate.id} className="hover:bg-gray-50" data-testid={`deleted-estimate-${estimate.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {estimate.customer_last_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {estimate.customer_first_name || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {estimate.address}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(estimate.total_cost || estimate.pricing?.total)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {estimate.deleted_at ? new Date(estimate.deleted_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <Button
                          size="sm"
                          onClick={() => handleRestore(estimate.id)}
                          className="bg-green-600 hover:bg-green-700"
                          data-testid={`restore-estimate-${estimate.id}`}
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Active Estimates Table */}
      {viewMode === 'active' && (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estimate
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Proposal Sent
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Follow-up
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Accepted
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Install Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEstimates.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <p className="text-lg font-medium mb-2">No estimates found</p>
                      <p className="text-sm">
                        {searchTerm ? 'Try adjusting your search' : 'Create your first estimate to get started'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredEstimates.map((estimate) => (
                  <tr key={estimate.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {estimate.customer_last_name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {estimate.customer_first_name || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {estimate.address}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(estimate.total_cost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {estimate.square_footage} sq ft
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {estimate.proposal_sent ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" data-testid={`sequence-badge-${estimate.id}`}>
                      {sequenceStatuses[estimate.id]?.status === 'active' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                          </span>
                          Active ({sequenceStatuses[estimate.id].emails_sent}/{sequenceStatuses[estimate.id].emails_total})
                        </span>
                      ) : sequenceStatuses[estimate.id]?.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          <Mail className="w-3 h-3" />
                          Done
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {estimate.accepted ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          No
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                        {formatDate(estimate.scheduled_install_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/dashboard/estimates/${estimate.id}`}>
                          <Button variant="ghost" size="sm">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Link to={`/dashboard/estimates/${estimate.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(estimate.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Summary Stats */}
      {viewMode === 'active' && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Total Estimates</div>
          <div className="text-2xl font-bold text-gray-900">{estimates.length}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Proposals Sent</div>
          <div className="text-2xl font-bold text-blue-600">
            {estimates.filter(e => e.proposal_sent).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Accepted</div>
          <div className="text-2xl font-bold text-green-600">
            {estimates.filter(e => e.accepted).length}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Total Value</div>
          <div className="text-2xl font-bold text-purple-600">
            {formatCurrency(estimates.reduce((sum, e) => sum + (e.total_cost || 0), 0))}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default EstimatesList;
